import { Close } from '@mui/icons-material';
import { Autocomplete, Box, Button, Fade, IconButton, Modal, Paper, TextField, Typography } from '@mui/material';
import React, { memo, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDebounce } from '../../hooks/useDebounce';
import { showErrorSnackbar, showSuccessSnackbar } from '../snackBar/snakbar';
import {
  useCreateAccountGroupMasterMutation,
  useListGroupMasterQuery,
  useUpdateAccountGroupMasterMutation,
} from '../../redux/apis/accountGroupMaster.api';
import { LoadingButton } from '@mui/lab';
import { useListAccountMasterv2Query } from '../../redux/apis/accountMaster.api';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  boxShadow: 24,
  outline: 'none',
  minWidth: 500,
  '@media (max-width:600px)': {
    minWidth: 300,
  },
};

function CreateAndEditGroupModal({
  open,
  setOpen,
  groupId,
  action,
  setSelectedRow,
  setNewLedgerResponse,
  page,
  tab,
  selectedRow,
  refetchGroupMaster,
}) {
  const companyDetails = useSelector((state) => state.companyReducer.companyDetails);
  const { data: groupMaster = [] } = useListGroupMasterQuery();
  const { data: accountMasterList = [] } = useListAccountMasterv2Query(undefined, { refetchOnMountOrArgChange: true });

  // Compute options
  const options = useMemo(() => {
    let list = [...groupMaster];
    const GSTArray = [31, 32, 33];

    if (groupId != null && action === 'edit') {
      list = list.filter((e) => e.accountgroupid !== Number(groupId));
    }

    // Filter out GST groups if registration type is 1
    if (companyDetails?.relatedcompanydetailsnew?.[0]?.registrationtype === 1) {
      list = list.filter((e) => !GSTArray.includes(e.accountgroupid));
    }

    if (tab === 'Receivables' || tab === 'Payables') {
      const id = tab === 'Receivables' ? '15' : '7';
      return list.filter((obj) => obj.path.split('/').includes(id)).sort((a, b) => a.accountgroupname.localeCompare(b.accountgroupname));
    }

    const rootId = selectedRow?.path?.split('/')[1];
    if (!rootId) {
      return list.sort((a, b) => a.accountgroupname.localeCompare(b.accountgroupname));
    }

    const expenseIds = ['20', '23', '25', '26', '27', '28'];
    const incomeIds = ['18', '22', '24'];

    if (expenseIds.includes(rootId)) {
      return list
        .filter((obj) => expenseIds.some((id) => obj.path.split('/').includes(id)))
        .sort((a, b) => a.accountgroupname.localeCompare(b.accountgroupname));
    }

    if (incomeIds.includes(rootId)) {
      return list
        .filter((obj) => incomeIds.some((id) => obj.path.split('/').includes(id)))
        .sort((a, b) => a.accountgroupname.localeCompare(b.accountgroupname));
    }

    return list.filter((obj) => obj.path.split('/').includes(rootId)).sort((a, b) => a.accountgroupname.localeCompare(b.accountgroupname));
  }, [groupMaster, companyDetails, page, tab, selectedRow]);

  // Form state
  const [form, setForm] = useState({ groupName: '', parent: null });
  const [errors, setErrors] = useState({ name: '', parent: '' });

  console.log({ form });
  console.log({ selectedRow: selectedRow });

  // open/edit
  useEffect(() => {
    if (!open) return;
    if (action === 'create' && groupId) {
      setForm((f) => ({ ...f, parent: groupId }));
    }
    if (action === 'edit') {
      const group = groupMaster.find((o) => o.accountgroupid === groupId);
      setForm({
        groupName: group?.accountgroupname || '',
        parent: group?.accountparentgroupid || null,
      });
    }
  }, [open, action, groupId, groupMaster]);

  // Hotkey Ctrl+S
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === 's' && open) {
        e.preventDefault();
        action === 'edit' ? handleEdit() : handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, action, form]);

  // Debounced check for name
  const debouncedCheck = useDebounce(
    (value) => {
      let exists = false;
      console.log({ selectedRow });
      console.log({ value });
      if (selectedRow?.name !== value) {
        exists = groupMaster.some((o) => o.accountgroupname.toLowerCase() === value.toLowerCase());
      }
      setErrors((err) => ({
        ...err,
        name: exists ? 'Group Name Already Exists' : '',
      }));
    },
    200,
    [groupMaster]
  );

  const formatName = (value) => value.replace(/\s+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

  const handleNameChange = useCallback(
    (e) => {
      const value = e.target.value.slice(0, 50);
      const formatted = formatName(value);
      setForm((f) => ({ ...f, groupName: formatted }));
      debouncedCheck(formatted);
    },
    [debouncedCheck]
  );

  const validate = () => {
    let valid = true;
    const newErrors = { name: '', parent: '' };

    if (!form.groupName.trim()) {
      newErrors.name = 'Group Name is Required';
      valid = false;
    } else if (selectedRow?.name !== form?.groupName) {
      if (groupMaster.some((o) => o.accountgroupname.toLowerCase().trim() === form.groupName.toLowerCase().trim())) {
        newErrors.name = 'Group Name Already Exists';
        valid = false;
      }
    } else if (accountMasterList.some((o) => o.accountname.toLowerCase().trim() === form.groupName.toLowerCase().trim())) {
      newErrors.name = 'This name already exists in the ledger';
      valid = false;
    }

    if (!form.parent) {
      newErrors.parent = 'Group is Required';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const [createGroup, { isLoading: creating }] = useCreateAccountGroupMasterMutation();
  const [updateGroup, { isLoading: updating }] = useUpdateAccountGroupMasterMutation();

  const handleSave = async () => {
    if (!validate()) return;
    try {
      const payload = {
        accountgroupname: form.groupName,
        accountparentgroupid: form.parent,
        accounttypecd: options.find((o) => o.accountgroupid === form.parent)?.accounttypecd,
      };
      const { data } = await createGroup({
        createAccountGroupMasterInput: payload,
      });
      if (data?.accountgroupid) {
        setNewLedgerResponse(data.accountgroupid);
        showSuccessSnackbar('New group created successfully.');
        refetchGroupMaster?.();
      } else throw new Error();
      handleClose();
    } catch {
      showErrorSnackbar('Failed to create the group. Please try again.');
    }
  };

  const handleEdit = async () => {
    if (!validate()) return;
    try {
      const details = groupMaster.find((o) => o.accountgroupid === groupId);
      const payload = {
        accountgroupid: details.accountgroupid,
        accountgroupname: form.groupName,
        accountparentgroupid: form.parent,
        accounttypecd: details.accounttypecd,
        companybranchid: details.companybranchid,
        is_active: details.is_active,
      };
      const { data } = await updateGroup({
        updateAccountGroupMasterInput: payload,
      });
      if (data?.statuscode === 'Success') {
        showSuccessSnackbar('Group updated successfully.');
        setNewLedgerResponse(null);
      } else throw new Error(data?.statusmessage);
      handleClose();
    } catch (err) {
      showErrorSnackbar(err.message || 'Update failed. No changes were made.');
    }
  };

  const handleClose = () => {
    setForm({ groupName: '', parent: null });
    setErrors({ name: '', parent: '' });
    setSelectedRow();
    setOpen(false);
  };

  return (
    <Modal open={open} onClose={handleClose} disableEnforceFocus>
      <Fade in={open}>
        <Paper sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'primary.main',
            }}
          >
            <Typography variant="h6" color="white" flexGrow={1}>
              {action === 'edit' ? 'Edit Group' : 'Add Group'}
            </Typography>
            <IconButton onClick={handleClose}>
              <Close htmlColor="#fff" />
            </IconButton>
          </Box>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              label="Group Name"
              size="small"
              value={form.groupName}
              onChange={handleNameChange}
              error={!!errors.name}
              helperText={errors.name}
            />
            <Box mt={2}>
              <Autocomplete
                fullWidth
                options={options}
                getOptionLabel={(o) => o.accountgroupname}
                value={options.find((o) => o.accountgroupid === form.parent) || null}
                onChange={(e, val) => {
                  setForm((f) => ({
                    ...f,
                    parent: val?.accountgroupid || null,
                  }));
                  setErrors((err) => ({
                    ...err,
                    parent: val ? '' : 'Group is Required',
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Select Group" size="small" error={!!errors.parent} helperText={errors.parent} />
                )}
              />
            </Box>
            <Box display="flex" justifyContent="flex-end" mt={4}>
              <Button variant="outlined" onClick={handleClose} sx={{ mr: 2 }}>
                Cancel
              </Button>
              <LoadingButton onClick={action === 'edit' ? handleEdit : handleSave} loading={creating || updating} variant="contained">
                {action === 'edit' ? 'Update' : 'Save'}
              </LoadingButton>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}

export default memo(CreateAndEditGroupModal);
